/**
 * Eine Warteschlange für ARKO-Leiste, Dokumentzeile und Diktat.
 * Der Chat-Workspace bindet den offenen Thread, solange er montiert ist.
 */
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Message } from '../InlineChat';
import {
  appendStoredArkoThreadMessages,
  readStoredArkoThreadMessages,
} from './portalArkoThreadStorage';
import { usePortalBackgroundJobs } from './usePortalBackgroundJobs';

type LiveThreadBinding = {
  getThreadMessages: (threadId: string) => Message[] | undefined;
  appendToThread: (threadId: string, updater: (prev: Message[]) => Message[]) => void;
  onAnalysisPostedToChat?: () => void;
  activeThreadId: () => string | null;
};

type BackgroundJobsApi = ReturnType<typeof usePortalBackgroundJobs>;

const BackgroundJobsContext = createContext<BackgroundJobsApi | null>(null);

let liveBinding: LiveThreadBinding | null = null;
const liveListeners = new Set<() => void>();

export function bindPortalBackgroundLiveThread(next: LiveThreadBinding | null): void {
  liveBinding = next;
  liveListeners.forEach((fn) => fn());
}

export function readLiveAnalysisThreadId(): string | null {
  const id = liveBinding?.activeThreadId()?.trim() || '';
  return id || null;
}

export function subscribeLiveAnalysisThread(listener: () => void): () => void {
  liveListeners.add(listener);
  return () => liveListeners.delete(listener);
}

export function PortalBackgroundJobsProvider({
  customerId,
  webhookUrl,
  extraBody,
  mode,
  timeoutMs,
  children,
}: {
  customerId: string;
  webhookUrl: string;
  extraBody?: Record<string, unknown>;
  mode?: string;
  timeoutMs: number;
  children: React.ReactNode;
}) {
  const customerIdRef = useRef(customerId);
  customerIdRef.current = customerId;

  const getThreadMessages = useCallback((threadId: string) => {
    const fromLive = liveBinding?.getThreadMessages(threadId);
    if (fromLive) return fromLive;
    return readStoredArkoThreadMessages(customerIdRef.current, threadId);
  }, []);

  const appendToThread = useCallback((threadId: string, updater: (prev: Message[]) => Message[]) => {
    if (liveBinding) {
      liveBinding.appendToThread(threadId, updater);
      return;
    }
    appendStoredArkoThreadMessages(customerIdRef.current, threadId, updater);
  }, []);

  const onAnalysisPostedToChat = useCallback(() => {
    liveBinding?.onAnalysisPostedToChat?.();
  }, []);

  const jobs = usePortalBackgroundJobs({
    customerId,
    webhookUrl,
    timeoutMs,
    mode,
    extraBody,
    getThreadMessages,
    appendToThread,
    onAnalysisPostedToChat,
  });

  return (
    <BackgroundJobsContext.Provider value={jobs}>
      {children}
    </BackgroundJobsContext.Provider>
  );
}

export function usePortalBackgroundJobsContext(): BackgroundJobsApi {
  const jobs = useContext(BackgroundJobsContext);
  if (!jobs) {
    throw new Error('PortalBackgroundJobsProvider fehlt');
  }
  return jobs;
}

/** Sitz-Glocke liegt immer im Provider. Außerhalb bleibt die Glocke ohne Analyse-Zeilen. */
export function useOptionalPortalBackgroundJobs(): BackgroundJobsApi | null {
  return useContext(BackgroundJobsContext);
}

/** Offenhalten, bis der Workspace den Thread bindet oder wieder löst. */
export function useLiveAnalysisThreadVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => subscribeLiveAnalysisThread(() => setVersion((n) => n + 1)), []);
  return version;
}

export function useBindPortalBackgroundLiveThread(binding: LiveThreadBinding): void {
  const bindingRef = useRef(binding);
  bindingRef.current = binding;
  useLayoutEffect(() => {
    bindPortalBackgroundLiveThread({
      getThreadMessages: (threadId) => bindingRef.current.getThreadMessages(threadId),
      appendToThread: (threadId, updater) => bindingRef.current.appendToThread(threadId, updater),
      onAnalysisPostedToChat: () => bindingRef.current.onAnalysisPostedToChat?.(),
      activeThreadId: () => bindingRef.current.activeThreadId(),
    });
    return () => bindPortalBackgroundLiveThread(null);
  }, []);
}
